import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import NavbarSidebarWrapper from "../../components/navbarSidebarWrapper";
import actions from "../../redux/actions";
import { useSelector } from "react-redux";
import { notifyError, notifySuccess } from "../../components/ToastContext";
import ScreenLoader from "../../components/ScreenLoader";
import * as BsIcons from "react-icons/bs";

// Screen Component: RateUpdate allows manual odds adjustment for match markets
const RateUpdate = () => {
  // Retrieve URL parameter variables
  const { marketid, eventId, sportId } = useParams();
  const navigate = useNavigate();
  
  // State definitions
  const [loader, setLoader] = useState(false);
  const [rates, setRates] = useState([]); // Array holds overridden rates loaded from database
  const [marketInfo, setMarketInfo] = useState(null); // Current market details (runners list)

  // Selector for event details stored in redux
  const eventDetail = useSelector((state) => state.bookEvent.eventDetailData);

  // Function: Load market runners and existing rate overrides from API
  const loadData = async () => {
    setLoader(true);
    try {
      // Fetch match event details to retrieve runner schema
      await actions.getEventDetail(eventId);
      
      // Fetch manual rates from C# Web API
      const res = await actions.GetManualRates(marketid);
      if (res && res.data && res.data.Status) {
        setRates(res.data.Result || []);
      } else {
        setRates([]);
      }
    } catch (error) {
      notifyError("Failed to fetch rates: " + error.message);
    } finally {
      setLoader(false);
    }
  };

  // Trigger data load when component mounts or parameters change
  useEffect(() => {
    loadData();
  }, [marketid, eventId]);

  // Hook: Extract specific market information from the global list
  useEffect(() => {
    if (eventDetail && eventDetail.length > 0) {
      const match = eventDetail.find(
        (m) => m.MarketId === marketid || m.MarketId.replace(/B$/, "") === marketid.replace(/B$/, "")
      );
      if (match) {
        setMarketInfo(match);
      }
    }
  }, [eventDetail, marketid]);

  // Handler: Manage state changes for input fields (back price / lay price)
  const handleRateChange = (selectionId, runnerName, field, value) => {
    setRates((prevRates) => {
      const existingRateIdx = prevRates.findIndex((r) => r.SelectionId === selectionId);
      if (existingRateIdx > -1) {
        const updated = [...prevRates];
        updated[existingRateIdx] = {
          ...updated[existingRateIdx],
          [field]: value,
        };
        return updated;
      } else {
        return [
          ...prevRates,
          {
            MarketId: marketid,
            EventId: eventId,
            SelectionId: selectionId,
            RunnerName: runnerName,
            BackPrice: field === "BackPrice" ? value : "",
            LayPrice: field === "LayPrice" ? value : "",
            IsActive: true,
            Status: true,
          },
        ];
      }
    });
  };

  // Handler: Submit overridden prices to the C# WebAPI sequentially
  const handleSave = async () => {
    setLoader(true);
    try {
      let savedCount = 0;
      for (const rate of rates) {
        const payload = {
          MarketId: marketid,
          EventId: eventId,
          SelectionId: rate.SelectionId,
          RunnerName: rate.RunnerName,
          BackPrice: rate.BackPrice || "0",
          LayPrice: rate.LayPrice || "0",
          IsActive: rate.IsActive !== undefined ? rate.IsActive : true,
          Status: rate.Status !== undefined ? rate.Status : true,
        };
        await actions.SaveManualRate(payload);
        savedCount++;
      }
      notifySuccess(`Successfully saved manual rates for ${savedCount} runners!`);
      loadData();
    } catch (error) {
      notifyError("Failed to save rates: " + error.message);
    } finally {
      setLoader(false);
    }
  };

  // Handler: Deactivate all overrides and reset back/lay prices
  const handleClear = async () => {
    setLoader(true);
    try {
      for (const rate of rates) {
        const payload = {
          MarketId: marketid,
          EventId: eventId,
          SelectionId: rate.SelectionId,
          RunnerName: rate.RunnerName,
          BackPrice: "0",
          LayPrice: "0",
          IsActive: false,
          Status: false,
        };
        await actions.SaveManualRate(payload);
      }
      notifySuccess("All manual overrides cleared successfully!");
      loadData();
    } catch (error) {
      notifyError("Failed to clear rates: " + error.message);
    } finally {
      setLoader(false);
    }
  };

  return (
    <NavbarSidebarWrapper>
      <div className="container-fluid">
        {/* Header navigation bar */}
        <div className="d-flex align-items-center mb-4">
          <button className="back-btn mr-3" onClick={() => navigate("/view-match")}>
            <BsIcons.BsArrowReturnLeft className="mr-1" /> Back
          </button>
          <h1 className="h3 mb-0 text-gray-800">Rate Update - Manual Rates</h1>
        </div>

        {loader && <ScreenLoader />}

        {/* Form view */}
        {!loader && marketInfo && (
          <div className="card shadow mb-4">
            <div className="card-header py-3 bg-primary text-white d-flex justify-content-between align-items-center">
              <h6 className="m-0 font-weight-bold">{marketInfo.MatchName || "Match Details"}</h6>
              <span className="badge badge-light text-primary">{marketInfo.MarketType}</span>
            </div>
            <div className="card-body">
              <div className="row mb-3">
                <div className="col-md-6">
                  <strong>Market ID:</strong> {marketid}
                </div>
                <div className="col-md-6 text-right">
                  <strong>Event ID:</strong> {eventId}
                </div>
              </div>

              {/* Runners table */}
              <div className="table-responsive">
                <table className="table table-bordered" width="100%" cellSpacing="0">
                  <thead>
                    <tr className="bg-light">
                      <th>Runner Name</th>
                      <th>Selection ID</th>
                      <th style={{ width: "20%" }}>Back Price</th>
                      <th style={{ width: "20%" }}>Lay Price</th>
                    </tr>
                  </thead>
                  <tbody>
                    {marketInfo.runners &&
                      marketInfo.runners.map((runner, index) => {
                        const currentRate = rates.find(
                          (r) => r.SelectionId === runner.selectionId.toString()
                        ) || { BackPrice: "", LayPrice: "" };

                        return (
                          <tr key={index}>
                            <td className="align-middle">
                              <strong>{runner.RunnerName}</strong>
                            </td>
                            <td className="align-middle text-muted">{runner.selectionId}</td>
                            <td>
                              <input
                                type="number"
                                className="form-control"
                                placeholder="Back Price"
                                step="0.01"
                                value={currentRate.BackPrice === "0" ? "" : currentRate.BackPrice}
                                onChange={(e) =>
                                  handleRateChange(
                                    runner.selectionId.toString(),
                                    runner.RunnerName,
                                    "BackPrice",
                                    e.target.value
                                  )
                                }
                              />
                            </td>
                            <td>
                              <input
                                type="number"
                                className="form-control"
                                placeholder="Lay Price"
                                step="0.01"
                                value={currentRate.LayPrice === "0" ? "" : currentRate.LayPrice}
                                onChange={(e) =>
                                  handleRateChange(
                                    runner.selectionId.toString(),
                                    runner.RunnerName,
                                    "LayPrice",
                                    e.target.value
                                  )
                                }
                              />
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>

              {/* Form buttons */}
              <div className="mt-4 d-flex justify-content-end">
                <button className="btn btn-secondary mr-2" onClick={handleClear}>
                  Clear Rates
                </button>
                <button className="btn btn-primary" onClick={handleSave}>
                  Save Manual Rates
                </button>
              </div>
            </div>
          </div>
        )}

        {!loader && !marketInfo && (
          <div className="card shadow p-4 text-center">
            <p className="text-muted mb-0">No market details found for the selected Match.</p>
          </div>
        )}
      </div>
    </NavbarSidebarWrapper>
  );
};

export default RateUpdate;
